import { CircularProgress, Stack, useTheme } from "@mui/material";
import { AskHelenaErrorBanner } from "./AskHelenaErrorBanner";
import { AskHelenaThread } from "./AskHelenaThread";
import { ChatEmptyState } from "./ChatEmptyState";

interface ChatThreadAreaProps {
  /** A history thread is being fetched — nothing to show yet. */
  isLoading: boolean;
  /** Set when that fetch failed; the thread stays empty. */
  errorMessage: string | null;
  hasLiveConversation: boolean;
  onAskSuggestion: (question: string) => void;
  onApprovePendingWrite: (pendingWriteId: string) => void;
  onDeclinePendingWrite: (pendingWriteId: string) => void;
}

/**
 * Picks what fills the chat panel above the composer. Split out of ChatPanel so
 * the precedence between the four states reads in one place.
 */
export const ChatThreadArea = ({
  isLoading,
  errorMessage,
  hasLiveConversation,
  onAskSuggestion,
  onApprovePendingWrite,
  onDeclinePendingWrite,
}: ChatThreadAreaProps) => {
  const theme = useTheme();

  // Gutter lives here rather than on the panel so the header and the sidebar
  // toggle keep their own alignment. flex/minHeight pass straight through, so
  // the scroll container inside each branch is unchanged.
  const content = () => {
    if (isLoading) {
      return (
        <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <CircularProgress
            size={24}
            aria-label="Loading conversation"
            sx={{ color: theme.palette.primary.tint }}
          />
        </Stack>
      );
    }

    if (errorMessage) {
      return (
        <Stack sx={{ flex: 1, justifyContent: "center", p: "24px 0" }}>
          <AskHelenaErrorBanner message={errorMessage} />
        </Stack>
      );
    }

    if (hasLiveConversation) {
      return (
        <AskHelenaThread
          onApprove={onApprovePendingWrite}
          onDecline={onDeclinePendingWrite}
        />
      );
    }

    return <ChatEmptyState onAskSuggestion={onAskSuggestion} />;
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0, minWidth: 0, px: "70px" }}>
      {content()}
    </Stack>
  );
};
